import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export function AdminLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/admin/session", {
          method: "GET",
          credentials: "same-origin",
        });
        const data = (await res.json()) as { authenticated?: boolean };
        if (cancelled) return;
        if (res.ok && data.authenticated) {
          navigate("/admin", { replace: true });
          return;
        }
      } catch {
        /* stay on login */
      }
      if (!cancelled) setCheckingSession(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Email and password are required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/admin/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ email: email.trim(), password }),
      });
      const data = (await res.json()) as { error?: string; success?: boolean; user?: { email?: string } };
      if (!res.ok) {
        setError(data.error ?? "Sign in failed");
        return;
      }
      if (data.success && data.user?.email) {
        setPassword("");
        navigate("/admin", { replace: true });
      }
    } catch {
      setError("Network error");
    } finally {
      setSubmitting(false);
    }
  }

  if (checkingSession) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p className="text-gray-400 text-sm">Loading…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white flex flex-col bg-black">
      <div className="flex shrink-0 justify-center px-6 pt-[max(1.5rem,env(safe-area-inset-top))] pb-6">
        <img
          src="/assets/kennedy-logo.webp"
          alt="Kennedy Ryon"
          className="max-w-[120px] h-auto"
        />
      </div>
      <div className="flex flex-1 flex-col items-center px-6 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold">Admin sign in</h1>
            <p className="text-gray-400 text-sm mt-2">Use your admin email and password</p>
          </div>
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="block text-sm text-white mb-1">
                Email
              </label>
              <input
                id="admin-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-sm text-white mb-1">
                Password
              </label>
              <input
                id="admin-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg bg-white/10 border border-white/20 px-3 py-2.5 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {error && <p className="text-sm text-red-400">{error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className={
                "w-full mt-1.5 inline-flex items-center justify-center gap-2 rounded-[10px] border-0 px-4 py-[15px] " +
                "bg-[#9EBB89] text-base font-bold text-black " +
                "transition-[transform,box-shadow,background-color,opacity] duration-[400ms] ease-in-out " +
                "hover:-translate-y-0.5 hover:bg-[#C7D9B7] hover:shadow-[0_10px_36px_rgba(199,217,183,0.25)] " +
                "disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0 disabled:hover:bg-[#9EBB89] disabled:hover:shadow-none"
              }
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
